// src/pages/RichiediNFPage.js
// Area Personale: ogni utente compila e consulta esclusivamente le proprie Richieste NF.
// Dopo l'invio resta modificabile ed eliminabile dal suo autore finché è «Da gestire»; da
// quando l'ufficio Acquisti la prende in gestione la gestisce solo lui (NF – ODA – Fornitori).
import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, serverTimestamp, runTransaction, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, X } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import ArticoliNF, { useListinoMateriali } from "../components/ArticoliNF";
import { useControlloDoppioni } from "../components/ControlloDoppioni";
import { articoliValidi, articoliDaTesto, descrizioneConPiuRighe } from "../utils/acquisti";
import AllegatoOffertaNF from "../components/AllegatoOffertaNF";
import ElencoNF from "../components/ElencoNF";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "../components/AvvisoBozza";

const URGENZA = ["Normale","Media","Urgente"];

// Numero progressivo NF — una transazione vera, non un conteggio letto e poi riscritto: due
// invii nello stesso istante non possono mai ottenere lo stesso numero.
async function prossimoNumeroNF(orgId) {
  const contatoreRef = doc(db, "contatori", `${orgId}_nf`);
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contatoreRef);
    const progressivo = (snap.exists() ? (snap.data().ultimo || 0) : 0) + 1;
    transaction.set(contatoreRef, { ultimo: progressivo, orgId }, { merge: true });
    return progressivo;
  });
}


const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

export function FormNF({ orgId, currentUser, nfEsistente, commessaFissa, commesseRecenti, onClose }) {
  const { verifica, finestra: finestraDoppioni } = useControlloDoppioni();
  const { addToast } = useToast();
  const [commesse, setCommesse] = useState([]);
  const [search, setSearch] = useState("");
  const [commessaSel, setCommessaSel] = useState(
    commessaFissa || (nfEsistente ? { id:nfEsistente.commessaId, name:nfEsistente.commessaNome, codiceCommessa:nfEsistente.codiceCommessa } : null)
  );
  const [form, setForm] = useState({
    dataRichiesta: nfEsistente?.dataRichiesta || toLocalDateStr(new Date()),
    fornitoreId: nfEsistente?.fornitoreId || "",
    fornitoreNome: nfEsistente?.fornitoreId ? (nfEsistente?.fornitoreNome || "") : "",
    descrizione: nfEsistente?.descrizione || "",
    rifOfferta: nfEsistente?.rifOfferta || "",
    linkOfferta: nfEsistente?.linkOfferta || "",
    allegatoOfferta: nfEsistente?.allegatoOfferta || null,
    urgenza: nfEsistente?.urgenza || "Normale",
    motivoUrgenza: nfEsistente?.motivoUrgenza || "",
    destinazione: nfEsistente?.destinazione || "",
    dettaglioDestinazioneMista: nfEsistente?.dettaglioDestinazioneMista || "",
    dataConsegna: nfEsistente?.dataConsegna || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  // Articoli (facoltativi): diventano le righe dell'ODA quando l'ufficio Acquisti lo crea
  const [articoli, setArticoli] = useState(()=>(nfEsistente?.articoli||[]).map(a=>({...a})));
  const listino = useListinoMateriali(orgId);
  // Fornitore: solo dall'anagrafica Fornitori oppure «N.D.» — mai testo libero (W435),
  // altrimenti i filtri si sporcano («Rossi», «Rossi srl», «ROSSI»).
  const [fornitori, setFornitori] = useState([]);
  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(query(collection(db,"fornitori"), where("organizationId","==",orgId)),
      snap => setFornitori(snap.docs.map(d=>({id:d.id,...d.data()})).filter(f=>!f.eliminato && f.denominazione)
        .sort((a,b)=>a.denominazione.localeCompare(b.denominazione,"it"))), () => setFornitori([]));
  }, [orgId]);
  // La descrizione si allunga da sola mentre si scrive (W435)
  const allunga = (el) => { if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight + 2, 420) + "px"; } };
  const trasforma = () => {
    const nuovi = articoliDaTesto(form.descrizione);
    if (!nuovi.length) { addToast("Scrivi prima la descrizione, una voce per riga","error"); return; }
    setArticoli(a => [...articoliValidi(a), ...nuovi]);
    addToast(`${nuovi.length} articoli pronti: controlla quantità e unità di misura`,"success");
  };
  // NF già inviata (ancora «Da gestire»): si salvano le modifiche senza ritirarla
  // dalla coda degli Acquisti — prima "Salva bozza" la riportava a "Da inviare".
  const giaInviata = nfEsistente?.statoRichiesta === "Inviata";
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "nf", orgId, nfEsistente?.id),
    valore: { commessa: commessaSel, form, articoli },
    info: { titolo: `${nfEsistente ? "Modifica richiesta NF" : "Nuova richiesta NF"}${commessaSel?.name ? ` — ${commessaSel.name}` : ""}`,
      vai: commessaFissa?.id ? { page: "project", projectId: commessaFissa.id, initialTab: "richiedi-nf" } : { page: "richiedi-nf" } },
    applica: d => {
      if (d.commessa !== undefined && !commessaFissa) setCommessaSel(d.commessa);
      if (d.form) setForm(f => ({ ...f, ...d.form }));
      if (Array.isArray(d.articoli)) setArticoli(d.articoli);
    },
  });

  useEffect(()=>{
    if (!orgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",orgId),where("statoCommessa","==","attiva")),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived && !p.isParent)));
  },[orgId]);

  const commesseFiltrate = commesse.filter(c=>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || (c.codiceCommessa||"").toLowerCase().includes(search.toLowerCase())
  ).sort((a,b)=>a.name?.localeCompare(b.name||"","it"));

  const salva = async (invia) => {
    if (!commessaSel) { addToast("Seleziona una commessa","error"); return; }
    if (!form.descrizione.trim()) { addToast("Descrizione obbligatoria","error"); return; }
    if (invia && form.urgenza==="Urgente" && !form.motivoUrgenza.trim()) { addToast("Devi specificare il motivo dell'urgenza per procedere","error"); return; }
    if (invia && form.destinazione==="Misto" && !form.dettaglioDestinazioneMista.trim()) { addToast("Indica quali voci vanno in ciascun luogo","error"); return; }
    // Più voci scritte solo nella descrizione: si propone di farne articoli (mai obbligatorio)
    if (invia && !articoliValidi(articoli).length && descrizioneConPiuRighe(form.descrizione)
      && await confirm("La descrizione ha più righe. Vuoi trasformarle in articoli? Ogni riga diventa un articolo, con quantità e unità di misura riconosciute da sole: controlli e poi invii.", { title: "Trasformare in articoli?", type: "info", confirmLabel: "Sì, trasforma", cancelLabel: "No, invia così" })) {
      trasforma(); return;
    }
    setSaving(true);
    // All'invio: in commessa c'è già lo stesso materiale (ODA, altra NF, magazzino, voce a mano)? (W408)
    let campiDoppioni = {};
    if (invia) {
      const art = articoliValidi(articoli);
      const esito = await verifica({ orgId, projectId: commessaSel.id, righe: art.length ? art : [{ descrizione: form.descrizione }],
        escludiDoc: nfEsistente?.id ? `nf:${nfEsistente.id}` : null, nonDoppioni: nfEsistente?.nonDoppioni || [] });
      if (!esito.procedi) { setSaving(false); return; }
      campiDoppioni = esito.campi;
    }
    try {
      if (nfEsistente) {
        // L'ufficio Acquisti potrebbe averla presa in carico mentre era aperta qui
        const ora = await getDoc(doc(db,"nuoveForniture",nfEsistente.id));
        if (ora.exists() && (ora.data().presaInCaricoDa || ora.data().odaId || ora.data().annullata || ora.data().eliminata)) {
          addToast("Nel frattempo l'ufficio Acquisti l'ha presa in gestione: per cambiare qualcosa scrivi nella chat della NF","error");
          setSaving(false); return;
        }
      }
      if (giaInviata) {
        await updateDoc(doc(db,"nuoveForniture",nfEsistente.id), {
          ...form, articoli: articoliValidi(articoli), ...campiDoppioni,
          commessaId: commessaSel.id, commessaNome: commessaSel.name, codiceCommessa: commessaSel.codiceCommessa||"",
          modificataDopoInvio: true, modificataDopoInvioAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        addToast("Modifiche salvate — la richiesta resta inviata all'ufficio Acquisti ✅","success");
        onClose(); setSaving(false); return;
      }
      const dati = {
        ...form, articoli: articoliValidi(articoli), tipo:"NF", ...campiDoppioni,
        commessaId: commessaSel.id, commessaNome: commessaSel.name, codiceCommessa: commessaSel.codiceCommessa||"",
        organizationId: orgId,
        compilatoreNome: currentUser?.displayName || "",
        statoRichiesta: invia ? "Inviata" : "Da inviare",
        ...(invia ? { inviataAt: serverTimestamp() } : {}),
        updatedAt: serverTimestamp(),
      };
      if (nfEsistente) {
        // Se sto inviando ora una bozza, assegna il numero progressivo solo in questo momento
        if (invia && !nfEsistente.numero) {
          dati.numero = await prossimoNumeroNF(orgId);
        }
        await updateDoc(doc(db,"nuoveForniture",nfEsistente.id), dati);
      } else {
        if (invia) {
          dati.numero = await prossimoNumeroNF(orgId);
        }
        await addDoc(collection(db,"nuoveForniture"), { ...dati, createdBy:currentUser.uid, createdAt:serverTimestamp() });
      }
      addToast(invia ? "NF inviata all'Ufficio Acquisti ✅" : "Bozza salvata 💾", "success");
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      {finestraDoppioni}
      <div className="modal" style={{maxWidth:640,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>📋 {nfEsistente?"Modifica richiesta NF":"Nuova richiesta NF"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo={nfEsistente ? "Hai modifiche non salvate a questa richiesta NF" : "Hai una richiesta NF iniziata e non salvata"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        {!commessaFissa && (
        <div className="form-group" style={{marginBottom:14}}>
          <label style={lbl}>Commessa *</label>
          {commessaSel ? (
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#ede9fe",border:"2px solid #7c3aed",borderRadius:8}}>
              <span style={{flex:1,fontSize:13,fontWeight:700,color:"#7c3aed"}}>
                ✓ {commessaSel.codiceCommessa && `[${commessaSel.codiceCommessa}] `}{commessaSel.name}
              </span>
              <button type="button" onClick={()=>{setCommessaSel(null);setSearch("");}} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:0}}><X size={14}/></button>
            </div>
          ) : (
            <div>
              {commesseRecenti && commesseRecenti.length > 0 && (
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#9ca3af",marginBottom:6}}>Commesse recenti</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {commesseRecenti.map(c=>(
                      <button key={c.id} type="button" onClick={()=>{setCommessaSel(c);setSearch("");}}
                        style={{padding:"6px 12px",borderRadius:20,border:"1px solid #e9d5ff",background:"#faf5ff",
                          color:"#7c3aed",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        {c.codiceCommessa && `[${c.codiceCommessa}] `}{c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <input style={inp} value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca per numero o nome commessa..."/>
              {search && (
                <div style={{border:"1px solid #e5e7eb",borderRadius:8,maxHeight:180,overflowY:"auto",background:"#fff",marginTop:6}}>
                  {commesseFiltrate.length===0 ? (
                    <div style={{padding:"12px 14px",color:"#9ca3af",fontSize:13}}>Nessuna commessa trovata</div>
                  ) : commesseFiltrate.map(c=>(
                    <div key={c.id} onClick={()=>{setCommessaSel(c);setSearch("");}}
                      style={{padding:"10px 14px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f3f4f6",fontWeight:500}}
                      onMouseOver={e=>e.currentTarget.style.background="#f9f5ff"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                      {c.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{c.codiceCommessa}]</span>}
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><label style={lbl}>Data richiesta</label><input type="date" style={inp} value={form.dataRichiesta} onChange={e=>upd("dataRichiesta",e.target.value)}/></div>
          <div><label style={lbl}>Data consegna desiderata</label><input type="date" style={inp} value={form.dataConsegna} onChange={e=>upd("dataConsegna",e.target.value)}/></div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl} htmlFor="nf-descrizione">Descrizione *</label>
          <textarea id="nf-descrizione" ref={allunga} style={{...inp,minHeight:70,resize:"vertical",lineHeight:1.45}} value={form.descrizione}
            onChange={e=>{ upd("descrizione",e.target.value); allunga(e.target); }} placeholder={"Cosa serve, una voce per riga. Es.\n100 m cavo FG16OR16 3x2,5\n10 pz scatola 503"}/>
          <div style={{fontSize:11.5,color:"#7c3aed",marginTop:3}}>💜 Il campo si allunga da solo mentre scrivi; puoi anche allargarlo trascinando l'angolino in basso a destra ◢.</div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Articoli <span style={{fontWeight:400,color:"#9ca3af"}}>(facoltativi — diventano le righe dell'ordine al fornitore)</span></label>
          <div style={{fontSize:11.5,color:"#6d28d9",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"7px 10px",marginBottom:8,lineHeight:1.5}}>
            💜 Inserendo gli articoli (descrizione, quantità, unità di misura e, se c'è, codice) l'ufficio Acquisti può chiedere le offerte e preparare l'ordine subito, senza ricopiare nulla. Se hai poco tempo, scrivi almeno una voce per riga nella descrizione.
            {!articoliValidi(articoli).length && descrizioneConPiuRighe(form.descrizione) && (
              <button type="button" onClick={trasforma} style={{display:"block",marginTop:6,fontSize:12,fontWeight:700,color:"#6d28d9",background:"#fff",border:"1px solid #c4b5fd",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>🔄 Trasforma le righe della descrizione in articoli</button>
            )}
          </div>
          <ArticoliNF articoli={articoli} onChange={setArticoli} listino={listino}/>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><label style={lbl} htmlFor="nf-fornitore">Fornitore</label>
            <select id="nf-fornitore" style={inp} value={form.fornitoreId} onChange={e=>{ const f=fornitori.find(x=>x.id===e.target.value); setForm(v=>({...v,fornitoreId:f?.id||"",fornitoreNome:f?.denominazione||""})); }}>
              <option value="">N.D. — non lo so / lo sceglie l'ufficio Acquisti</option>
              {fornitori.map(f=><option key={f.id} value={f.id}>{f.denominazione}</option>)}
            </select></div>
          <div>
            <label style={lbl}>Luogo di consegna</label>
            <select style={inp} value={form.destinazione} onChange={e=>upd("destinazione",e.target.value)}>
              <option value="">— Seleziona —</option>
              <option value="Ns sede">Ns sede</option>
              <option value="Cantiere">Cantiere</option>
              <option value="Ritiro presso fornitore">Ritiro presso fornitore</option>
              <option value="Misto">Misto — alcune voci in un luogo, altre in un altro</option>
            </select>
          </div>
        </div>

        {form.destinazione==="Misto" && (
          <div style={{marginBottom:14}}>
            <label style={lbl}>Indica quali voci vanno dove *</label>
            <textarea style={{...inp,minHeight:60,resize:"vertical"}} value={form.dettaglioDestinazioneMista}
              onChange={e=>upd("dettaglioDestinazioneMista",e.target.value)}
              placeholder={"Es.\nIn officina: 10 tubi corrugati, 2 quadri elettrici\nIn cantiere: 5 metri di cavo, il resto del materiale"}/>
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><label style={lbl}>Rif. Offerta</label><input style={inp} value={form.rifOfferta} onChange={e=>upd("rifOfferta",e.target.value)}/></div>
          <div><label style={lbl}>Link/PDF Offerta</label>
            <AllegatoOffertaNF orgId={orgId} inp={inp} link={form.linkOfferta} onLink={v=>upd("linkOfferta",v)} allegato={form.allegatoOfferta} onAllegato={v=>upd("allegatoOfferta",v)}/></div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Urgenza</label>
          <div style={{display:"flex",gap:8}}>
            {URGENZA.map(u=>(
              <button key={u} type="button" onClick={()=>upd("urgenza",u)}
                style={{flex:1,padding:"7px 0",border:`2px solid ${form.urgenza===u?(u==="Urgente"?"#dc2626":"#7c3aed"):"#e5e7eb"}`,
                  borderRadius:8,background:form.urgenza===u?(u==="Urgente"?"#fef2f2":"#faf5ff"):"#fff",
                  color:form.urgenza===u?(u==="Urgente"?"#dc2626":"#7c3aed"):"#6b7280",fontWeight:form.urgenza===u?700:400,
                  cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:13}}>
                {u==="Urgente"?"🚨 Urgente":u}
              </button>
            ))}
          </div>
        </div>
        {form.urgenza==="Urgente" && (
          <div style={{marginBottom:14}}>
            <label style={{...lbl,color:"#dc2626",fontWeight:700}}>🚨 Motivo dell'urgenza * <span style={{fontWeight:400,color:"#9ca3af"}}>(obbligatorio per inviare)</span></label>
            <textarea style={{...inp,minHeight:56,resize:"vertical",borderColor:form.motivoUrgenza.trim()?"#e5e7eb":"#fca5a5",background:form.motivoUrgenza.trim()?"#fff":"#fef2f2"}}
              value={form.motivoUrgenza} onChange={e=>upd("motivoUrgenza",e.target.value)} placeholder="Spiega perché questa richiesta è urgente..."/>
          </div>
        )}

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          {giaInviata ? (
            <button type="button" className="btn btn-primary" disabled={saving} onClick={()=>bozza.salvaCon(()=>salva(true))}>{saving?"Salvataggio...":"💾 Salva modifiche"}</button>
          ) : (<>
            <button type="button" className="btn btn-secondary" disabled={saving} onClick={()=>bozza.salvaCon(()=>salva(false))}>💾 Salva bozza</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={()=>bozza.salvaCon(()=>salva(true))}>{saving?"Invio...":"📤 Invia richiesta"}</button>
          </>)}
        </div>
      </div>
    </div>
  );
}

export default function RichiediNFPage() {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [nfs, setNfs] = useState([]);

  // Commesse recenti: estratte dalle proprie ultime NF già caricate, nessuna nuova chiamata a Firestore
  const commesseRecenti = useMemo(() => {
    const viste = new Set();
    const elenco = [];
    for (const nf of nfs) {
      if (!nf.commessaId || viste.has(nf.commessaId)) continue;
      viste.add(nf.commessaId);
      elenco.push({ id: nf.commessaId, name: nf.commessaNome, codiceCommessa: nf.codiceCommessa });
      if (elenco.length >= 5) break;
    }
    return elenco;
  }, [nfs]);
  const [showForm, setShowForm] = useState(false);
  const [editNf, setEditNf] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    return onSnapshot(
      query(collection(db,"nuoveForniture"), where("organizationId","==",effectiveOrgId), where("createdBy","==",currentUser.uid)),
      snap => {
        const lista = snap.docs.map(d=>({id:d.id,...d.data()}));
        lista.sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0));
        setNfs(lista);
      }
    );
  }, [effectiveOrgId, currentUser]);

  return (
    <div style={{padding:20, height:"100%", overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:10,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0}}>📋 Richiedi NF</h3>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary"
          style={{display:"flex",alignItems:"center",gap:5,background:"#7c3aed",borderColor:"#7c3aed",padding:"5px 12px",fontSize:12}}>
          <Plus size={13}/> Nuova richiesta
        </button>
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{nfs.length} richiesta/e create da te</span>
      </div>

      <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#6b21a8"}}>
        💜 <strong>Suggerimento Nexiora</strong> — Quando la Richiesta NF è riferita ad una commessa specifica, conviene crearla direttamente dall'interno della commessa: resterà automaticamente collegata, mantenendo ordinata tutta la documentazione. Questa sezione è pensata soprattutto per chi lavora sul campo e vuole creare una richiesta al volo, senza dover prima aprire la commessa.
      </div>

      <ElencoNF nfs={nfs} orgId={effectiveOrgId} currentUser={currentUser} onModifica={setEditNf} vuoto={(
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📋</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta NF creata da te per ora</div>
        </div>
      )}/>

      {showForm && <FormNF orgId={effectiveOrgId} currentUser={currentUser} commesseRecenti={commesseRecenti} onClose={()=>setShowForm(false)}/>}
      {editNf && <FormNF orgId={effectiveOrgId} currentUser={currentUser} nfEsistente={editNf} onClose={()=>setEditNf(null)}/>}
    </div>
  );
}
